import { SendCMD } from "./HTTPRequest";
import { GetHallServerUrl } from "../data/urls";

// Keep the existing hall transport ({cmd,msg}); BANK ownership is set by the
// server command, never by a client-supplied business_type.
export const bankRequest = async (cmd, data) => {
  let response;
  try {
    // Merchant payments retain the ordinary recharge/withdrawal request timeout.
    response = ["bankRecharge", "bankWithdraw"].includes(cmd) ? await SendCMD(cmd, data)
      : await SendCMD(cmd, data, { timeout: 20000 });
  } catch (cause) {
    const error = new Error("bank_network_error");
    error.code = "bank_network_error";
    error.uncertain = true;
    throw error;
  }
  if (!response || typeof response !== "object" || Array.isArray(response) ||
    (cmd.startsWith("bank") && ![true, 1, false, 0].includes(response.status))) {
    const error = new Error("bank_invalid_response");
    error.code = "bank_invalid_response";
    error.uncertain = true;
    throw error;
  }
  if (response.status === false || response.status === 0) {
    const error = new Error(response?.errdesc || "bank_request_failed");
    error.code = response?.errdcode || "bank_request_failed";
    // A failed transfer can have completed its bank lock before a downstream
    // service became unavailable. The caller retains its ID on every failure.
    error.uncertain = false;
    throw error;
  }
  return response;
};

export const bankData = (response) => response?.data ?? response;
export const bankRows = (response) => {
  const data = bankData(response);
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(response?.rows)) return response.rows;
  throw new Error("bank_invalid_response");
};
export const bankPagination = (response, page = 1, limit = 50) => {
  const rows = bankRows(response);
  const data = bankData(response);
  const pages = Number(data?.total_page ?? data?.total_pages ?? response?.total_page);
  const total = Number(data?.total ?? response?.total);
  const totalPages = Number.isInteger(pages) && pages >= 0 ? pages
    : Number.isInteger(total) && total >= 0 ? Math.ceil(total / limit) : null;
  return { rows, totalPages, hasNext: totalPages === null ? rows.length >= limit : page < totalPages };
};

// Parse decimal text without multiplying a binary floating point amount.
export const parseBankAmount = (input) => {
  const text = String(input ?? "").trim();
  if (!/^(?:\d+(?:\.\d{0,2})?|\.\d{1,2})$/.test(text)) return null;
  const [whole, decimal = ""] = text.split(".");
  const cents = Number(whole || 0) * 100 + Number(`${decimal}00`.slice(0, 2));
  return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
};
export const formatBankMoney = (value) => {
  if (value == null || value === "") return "—";
  const cents = Number(value);
  if (!Number.isSafeInteger(cents)) return "—";
  const absolute = Math.abs(cents);
  return `${cents < 0 ? "-" : ""}${Math.floor(absolute / 100)}.${String(absolute % 100).padStart(2, "0")}`;
};
export const bankWithdrawalFee = (cents, bps = 500) => {
  if (!Number.isSafeInteger(cents) || cents < 0 || !Number.isInteger(bps) || bps < 0 || bps > 10000) return null;
  // Split the multiplication so even safe large cent amounts stay exact.
  return Math.floor(cents / 10000) * bps + Math.floor(((cents % 10000) * bps + 5000) / 10000);
};

const storageKey = (user, kind) => {
  const rid = user?.rid ?? user?.money?.rid;
  if (rid == null || String(rid) === "") throw new Error("bank_account_required");
  return `bank:${encodeURIComponent(GetHallServerUrl())}:${encodeURIComponent(rid)}:${kind}`;
};
const readStored = (key) => {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch (error) { throw new Error("bank_storage_error"); }
};
const writeStored = (key, value) => {
  try {
    const text = JSON.stringify(value);
    localStorage.setItem(key, text);
    if (localStorage.getItem(key) !== text) throw new Error("bank_storage_error");
  } catch (error) { throw new Error("bank_storage_error"); }
};
const validTransfer = (value) => value && /^[A-Za-z0-9:_-]{1,64}$/.test(value.transfer_id)
  && Number.isSafeInteger(value.amount) && value.amount > 0;

export const getPendingBankTransfer = (user) => {
  let value;
  try { value = readStored(storageKey(user, "transfer")); }
  catch (error) { throw new Error("bank_storage_error"); }
  if (value && !validTransfer(value)) throw new Error("bank_storage_error");
  return value;
};
export const savePendingBankTransfer = (user, transfer) => {
  if (!validTransfer(transfer)) throw new Error("bank_invalid_amount");
  const current = getPendingBankTransfer(user);
  if (current && (current.transfer_id !== transfer.transfer_id || current.amount !== transfer.amount)) {
    throw new Error("bank_transfer_pending");
  }
  const saved = { transfer_id: transfer.transfer_id, amount: transfer.amount };
  writeStored(storageKey(user, "transfer"), saved);
  return saved;
};
export const clearPendingBankTransfer = (user, id) => {
  const current = getPendingBankTransfer(user);
  if (current?.transfer_id === id) localStorage.removeItem(storageKey(user, "transfer"));
};
export const createBankTransferId = () => {
  const random = new Uint32Array(4);
  window.crypto.getRandomValues(random);
  return `bank_${Date.now().toString(36)}_${Array.from(random, (value) => value.toString(16).padStart(8, "0")).join("")}`;
};

// Separate from ordinary recharge attribution. Retain completed order IDs too:
// revisiting a payment return URL must never report a bank order as first deposit.
export const rememberBankRecharge = (user, orderId) => {
  if (!orderId) throw new Error("bank_invalid_response");
  const key = storageKey(user, "recharge_orders");
  const previous = readStored(key) || [];
  if (!Array.isArray(previous)) throw new Error("bank_storage_error");
  const id = String(orderId);
  writeStored(key, previous.includes(id) ? previous : [...previous, id]);
};
export const isBankRecharge = (user, orderId) => {
  if (!user || !orderId) return false;
  try {
    const ids = readStored(storageKey(user, "recharge_orders"));
    return Array.isArray(ids) && ids.includes(String(orderId));
  } catch (error) { return false; }
};

export const bankRechargeReturnPath = (orderId) =>
  `/bank?view=history&order_type=recharge&wait_order_id=${encodeURIComponent(orderId)}`;

// Recover the order's server-owned business after checkout, including when
// browser storage was unavailable. Read-only searches never create a new order.
export const resolveRechargeBusiness = async (user, orderId, request = bankRequest) => {
  if (isBankRecharge(user, orderId)) return "BANK";
  let settled = false;
  const search = async (bank) => {
    let page = 1;
    const seen = new Set();
    while (!settled) {
      const response = await request(bank ? "bankOrders" : "payOrders", {
        token: user.token, order_type: bank ? "recharge" : "deposit", page,
        ...(bank ? { limit: 50 } : { range_type: "30days" }),
      });
      const pagination = bankPagination(response, page, bank ? 50 : 10);
      const order = pagination.rows.find((row) => String(row.order_id) === String(orderId));
      if (order) return bank || order.business_type === "BANK" ? "BANK" : "NORMAL";
      const signature = JSON.stringify(pagination.rows.map((row) => row.order_id));
      if (!pagination.hasNext || !pagination.rows.length || seen.has(signature)) return null;
      seen.add(signature);
      page += 1;
    }
    return null;
  };
  return new Promise((resolve) => {
    let remaining = 2;
    const complete = (type) => {
      remaining -= 1;
      if (!settled && (type || remaining === 0)) {
        settled = true;
        resolve(type || null);
      }
    };
    search(true).then(complete, () => complete(null));
    search(false).then(complete, () => complete(null));
  });
};

export const bankTurnoverRemaining = (withdrawData) => {
  const remaining = Number(withdrawData?.bank_turnover_remaining);
  return Number.isSafeInteger(remaining) && remaining > 0 ? remaining : 0;
};
